/**
 * What changed, newest first.
 *
 * Entries carry Japanese and English only. Nineteen translations of a running
 * changelog would go stale within a week, and a stale changelog is worse than
 * an English one — so everything else falls back to English.
 *
 * Add new entries at the top. Keep each line to one user-visible change; the
 * plumbing behind it belongs in the repo, not here.
 */

export interface ChangelogEntry {
  /** ISO date, YYYY-MM-DD. */
  date: string;
  ja: string[];
  en: string[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    date: "2026-09-08",
    ja: [
      "PMC の湧きの目安を出せるようにしました（サイドバーの「湧きの目安を表示」）。10 マップ・192 か所、入口の名前つき",
      "トランジットを脱出口と同じスイッチで出せるようにしました（10 マップ・28 か所）",
      "左のパネルを畳めるようにしました。地図の左端のタブで開閉できます",
      "ペンを持ったまま、マウスの中ボタンを押しながら地図を動かせるようにしました",
      "Factory の地下（Tunnels）が地上に隠れて見えなかったのを直しました",
      "Discord を開かなくても、ブラウザから直接使えるようにしました（トップページの「ブラウザで開く」）。出てきた URL を渡せば、通話に入っていない人も同じ地図に来られます",
      "1 つの部屋に残せる線の本数を 40 本から 150 本に増やしました。長く説明していると古い線から消えていた不具合の対処です",
    ],
    en: [
      "Roughly where a PMC raid starts, on 10 maps and 192 places, with the game's own entry names (\"Show where you spawn\" in the sidebar)",
      "Transits are drawn with the extracts, on the same switch: 28 of them across 10 maps",
      "The left panel folds away — the tab on the edge of the map opens and shuts it",
      "The middle mouse button pans the map without putting the pen away",
      "Fixed Factory's underground floor being drawn beneath the factory, which made it unreadable",
      "The map can be opened straight from a browser, without going through Discord (\"Open in the browser\" on the front page). Hand the address to anyone and they land on the same map",
      "A room now holds 150 lines instead of 40, so a long session stops quietly losing the oldest ones",
    ],
  },
  {
    date: "2026-08-27",
    ja: [
      "フリーハンドで線を引けるペンを追加しました（D キー）。色は 8 色から選べます",
      "消しゴムを追加しました（X キー）。押す・なぞるとピンも線もまとめて消せます",
      "サイドバーに「ピンと線の一覧」を追加しました。名前の変更・削除と、押すと地図がそこへ寄ります",
    ],
    en: [
      "A pen for drawing freehand lines (D). Pick from eight colours",
      "A rubber (X). Press or drag over anything to take pins and lines off the map",
      "A \"Pins and lines\" list in the sidebar: rename, remove, or press one to bring the map to it",
    ],
  },
  {
    date: "2026-08-25",
    ja: [
      "置いたピンをドラッグで動かせるようにしました。消して置き直す必要はもうありません。他の人のピンも動かせます（「自分」マーカーだけは本人のみ）",
    ],
    en: [
      "Pins can be dragged to a new spot — no more deleting and re-placing. You can move other people's pins too; only a \"me\" marker is limited to its owner",
    ],
  },
  {
    date: "2026-08-25",
    ja: [
      "マップを切り替えると画面が真っ黒になることがある不具合を修正しました。真っ黒になって諦めた方は、もう一度試してみてください",
      "起動できず 502 になることがある不具合も修正しました。開き直すたびに Discord の認証を取り直しており、人が多い時間帯に制限に掛かっていたのが原因です",
      "万一また表示できなくなっても、真っ黒ではなく照合番号付きのメッセージが出るようになりました。内容は自動で作者へ届くので、報告がなくても原因を追えます",
    ],
    en: [
      "Fixed a crash that could blank the whole screen when switching maps. If you hit a black rectangle and gave up, try again",
      "Fixed a second failure that could stop it starting at all (a 502). Every reload was re-doing the Discord sign-in, which hit a rate limit at busy times",
      "If something does break now you get a message with a reference code instead of a black rectangle, and the details reach the author on their own — so a problem can be traced even when nobody reports it",
    ],
  },
  {
    date: "2026-08-25",
    ja: [
      "小窓のときに地図全体が入るところまで縮小できるようにしました",
      "小窓ではピンの詳細と投稿者名を出さないようにしました。狭い画面では地図の邪魔になるためです",
    ],
    en: [
      "In the small window the map can now zoom out far enough to show the whole place",
      "The detail card and the by-line under each pin are hidden there — at that size they cover the map they are describing",
    ],
  },
  {
    date: "2026-08-25",
    ja: [
      "画面共有と同時に使えるようになりました。誰かが画面共有を始めると、地図が操作できる小窓（ピクチャーインピクチャー）として残ります",
      "小窓のときはサイドバーを畳んで地図を広く使います。左上の ☰ で開けます",
    ],
    en: [
      "The map now survives a screen share: when someone starts sharing, it stays as an interactive picture-in-picture window instead of disappearing behind the stream",
      "In that small window the sidebar folds away so the map gets the room; the ☰ button top-left brings it back",
    ],
  },
  {
    date: "2026-08-25",
    ja: [
      "Tarkov が対応している19言語に対応しました。Discord の言語設定に合わせて自動で切り替わり、サイドバーから手動でも選べます",
      "更新履歴をこのページに載せました",
    ],
    en: [
      "Added the 19 languages Escape from Tarkov ships an interface in. It follows your Discord language setting, and you can override it in the sidebar",
      "Put this update log on the front page",
    ],
  },
  {
    date: "2026-08-25",
    ja: [
      "招待リンクを追加しました。ボイスチャンネルにいない人も地図に呼べます（20人まで・3時間・いつでも無効化可）",
      "「別ウィンドウで開く」を追加しました。画面共有を見ながら、地図だけ別の画面に置けます",
      "アプリ内から要望・不具合を送れるようにしました",
      "使い方のページを用意しました",
    ],
    en: [
      "Added invite links, so people who are not on the voice call can join the map (up to 20, three hours, revocable at any time)",
      "Added \"open in a window\", so the map can sit on a second screen while Discord shows a stream",
      "You can now send feedback to the author from inside the app",
      "Wrote a how-to page",
    ],
  },
  {
    date: "2026-08-25",
    ja: [
      "ピンの色を種類ごとに変えました。丸の塗りが種類、縁が置いた人を表します",
      "ポップアウトしても同じ部屋に留まるようにしました（部屋のキーをボイスチャンネルに変更）",
      "同時に入れる部屋と人数に上限を入れました。満員でも、すでにいる部屋からは締め出されません",
    ],
    en: [
      "Pins are now coloured by kind: the fill says what it is, the ring says who placed it",
      "Popping the activity out no longer drops you into an empty room",
      "Added capacity limits. Being full never locks anyone out of a room they are already in",
    ],
  },
  {
    date: "2026-08-25",
    ja: ["常設ドメインへ移行し、Discord のアプリ認証を通しました。人数の多いサーバーでも起動できます"],
    en: [
      "Moved to a permanent domain and passed Discord's app verification, so it launches in servers of any size",
    ],
  },
];
